import styled from 'styled-components'

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sl-space-4);

  & + & {
    margin-top: var(--sl-space-4);
  }
`

export const SectionTitle = styled.strong`
  display: block;
  margin-top: var(--sl-space-5);
  margin-bottom: var(--sl-space-2);
  color: var(--sl-fg-base);
  font-weight: var(--sl-font-weight-medium);
`

export const FieldLabel = styled.span`
  display: block;
  margin-bottom: var(--sl-space-2);
  color: var(--sl-fg-base);
  font-weight: var(--sl-font-weight-medium);

  .required {
    color: var(--sl-fg-critical);
  }
`

export const ToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sl-space-2);
`

/**
 * Plain native button, not the shared ~/components/Buttons Button — that
 * component's own `isSelected` prop is accepted but has no CSS behind it
 * anywhere in the app, and giving it one here would affect every existing
 * consumer that already passes the prop. Scoped locally instead, matching
 * the prototype's pill-button multi-select (as opposed to the checkbox
 * rows used for Interests/Audience import below).
 */
export const ToggleButton = styled.button`
  border: var(--sl-border-base);
  border-radius: var(--sl-radius-2);
  padding: var(--sl-space-1) var(--sl-space-3);
  background: var(--sl-bg-base);
  color: var(--sl-fg-base);
  font: var(--sl-text-body-font);
  cursor: pointer;

  &:hover {
    border: var(--sl-border-accent-strong-hover);
  }

  &[data-selected='true'] {
    border: var(--sl-border-accent-strong);
    background: var(--sl-bg-accent);
    color: var(--sl-fg-accent);
  }
`

export const GatingOption = styled.div`
  border: var(--sl-border-base);
  border-radius: var(--sl-radius-2);
  padding: var(--sl-space-3);

  & + & {
    margin-top: var(--sl-space-2);
  }

  /* assets/scss/newtail.scss still carries Bootstrap 4's reboot rule
     (label { margin-bottom: .5rem }), and Shoreline's Checkbox declares no
     margin of its own, so those 8px land inside this card, below the row:
     a symmetric padding then reads as top-aligned (2026-09-15 demo,
     SPEC 004 item 3.2). Neutralised here rather than in the global rule,
     which every other form in the admin still depends on. */
  [data-sl-checkbox] {
    margin-bottom: 0;
  }
`

export const GatingArea = styled.div`
  margin-top: var(--sl-space-3);
  padding-top: var(--sl-space-3);
  border-top: var(--sl-border-base);
`

export const SupportText = styled.p`
  color: var(--sl-fg-base-soft);
  font: var(--sl-text-caption-1-font);
  margin: 0;
`

export const ErrorText = styled.p`
  color: var(--sl-fg-critical);
  font: var(--sl-text-caption-1-font);
  margin: var(--sl-space-2) 0 0;
`

// Same hint, but sitting directly under the segmentation cards rather than
// inside one — it was rendering flush against the card above (2026-09-15
// demo, SPEC 004 item 3.3). Spacing matches the prototype's
// `.dv360-audience-fallback`.
export const BroadAudienceHint = styled(SupportText)`
  margin-top: var(--sl-space-4);
`

// 2-column grid of category cards, matching the prototype
// (ads-design-prototypes, feat/offsite). Collapses to 1 column on narrow
// panels instead of a fixed breakpoint, since this renders inside a
// collapsible Panel of varying width.
export const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--sl-space-3);
`

export const CategoryCard = styled.div`
  border: var(--sl-border-base);
  border-radius: var(--sl-radius-2);

  &[data-expanded='true'] {
    border: var(--sl-border-accent-strong);
  }
`

export const CategoryHeader = styled.button`
  display: flex;
  align-items: flex-start;
  gap: var(--sl-space-3);
  width: 100%;
  padding: var(--sl-space-3);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
`

export const CategoryIconTile = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: var(--sl-radius-2);
  background: var(--sl-bg-accent);
  color: var(--sl-fg-accent);
`

export const CategoryTitleBlock = styled.span`
  flex: 1;
  display: block;
`

export const CategoryTitle = styled.strong`
  display: block;
  color: var(--sl-fg-base);
  font-weight: var(--sl-font-weight-medium);
`

export const CategoryDescription = styled.span`
  display: block;
  margin-top: var(--sl-space-05);
  color: var(--sl-fg-base-soft);
  font: var(--sl-text-caption-1-font);
`

export const CategoryHeaderMeta = styled.span`
  display: flex;
  align-items: center;
  gap: var(--sl-space-2);
  flex-shrink: 0;
  color: var(--sl-fg-base-soft);
`

// Circular selected-count badge — not the Shoreline Tag (a pill), to match
// the prototype's round counter.
export const CounterBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 var(--sl-space-1);
  border-radius: var(--sl-radius-full);
  background: var(--sl-bg-accent-strong);
  color: var(--sl-color-gray-0);
  font: var(--sl-text-caption-1-font);
  font-weight: var(--sl-font-weight-medium);
`

export const SubgroupList = styled.div`
  padding: 0 var(--sl-space-3) var(--sl-space-3);
  border-top: var(--sl-border-base);
  margin-top: 0;
  padding-top: var(--sl-space-3);
`
